import React, { useEffect, useRef, useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { Close as CloseIcon, Image as ImageIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  FormHelperText,
  IconButton,
  TextField,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { DirectUpload } from "@rails/activestorage";
import { ChangeHandler, SubmitHandler, useForm } from "react-hook-form";
import { makeStyles, withStyles } from "tss-react/mui";
import * as yup from "yup";

import { colors } from "@/theme/overrides/colors";
import { displayError } from "@/utils/errors";
import { useSnackBar } from "@hooks/useSnackBar";

import { Image } from "./AddBodyMeasurementModal";

const validationSchema = yup.object().shape({
  title: yup.string().required("Title is required"),
  file: yup.mixed().required("Plase, select photo"),
});

type Props = {
  visible: boolean;
  onClose: () => void;
  onAddImage: (image: Image) => void;
};

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(4, 8),
  },
  header: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerText: {
    fontSize: "24px",
    fontWeight: "bold",
  },
  upperLabel: {
    fontSize: "36px",
  },
  lowerLabel: {
    fontSize: "18px",
  },
  body: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: "5px dashed #EDF0F5",
    width: 300,
    height: 250,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    position: "relative",
  },
  optionField: {
    marginBottom: "14px",
  },
  image: {
    maxHeight: "100%",
    maxWidth: "100%",
    objectFit: "contain",
  },
  input: {
    width: "100%",
  },
  uploadButton: {
    background: theme.palette.common.black,
    opacity: 0.7,
    position: "absolute",
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    color: colors.white,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  hidden: {
    display: "none",
  },
}));

const StyledImage = withStyles(ImageIcon, {
  root: {
    backgroundColor: "#f2f3f5",
    color: colors.grey,
    height: "90px",
    width: "90px",
    padding: "14px",
    borderRadius: "45px",
  },
});

const StyledSaveButton = withStyles(Button, {
  root: {
    backgroundColor: colors.primary,
    width: "132px",
    height: "52px",
    "&:hover": {
      backgroundColor: colors.primary,
    },
  },
});

export const StyledTextField = withStyles(TextField, {
  root: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: colors.white,
    },
  },
});

type UploadFormValues = {
  title: string;
  file: File | null;
};

const UploadPhotoModal: React.FC<Props> = ({
  visible,
  onClose,
  onAddImage,
}) => {
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const [loading, setLoading] = useState<boolean>(false);
  const [hovered, setHovered] = useState<boolean>(false);
  const [currentSelectedPhoto, setCurrentSelectedPhoto] =
    useState<string | ArrayBuffer | null | undefined>();
  const [previewPhoto, setPreviewPhoto] = useState<string | undefined>();
  const [SimpleSnackBar, snackBarHelper] = useSnackBar();

  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
    setValue,
    reset,
    getValues,
  } = useForm<UploadFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      title: "",
      file: null,
    },
    mode: "onBlur",
    reValidateMode: "onBlur",
  });

  const { ref, ...rest } = register("file");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setPreviewPhoto(currentSelectedPhoto?.toString() ?? "");
  });

  const onChangePhoto: ChangeHandler = async (event) => {
    if (event?.target?.files) {
      const fileReader = new FileReader();
      fileReader.onloadend = (e) => {
        setCurrentSelectedPhoto(e.target?.result);
      };
      fileReader.readAsDataURL(event.target.files[0]);
    }
    setValue("file", event.target.files[0]);
  };

  const onSubmit: SubmitHandler<UploadFormValues> = (values) => {
    if (values.file === null) return;
    setLoading(true);
    const url = `${import.meta.env.VITE_API_URL}/api/direct_uploads`;
    const upload = new DirectUpload(values.file, url);

    upload.create((error, blob) => {
      if (error) {
        snackBarHelper.openSnackBar(
          displayError(error, "Error: Cannot upload photo.")
        );
      } else {
        snackBarHelper.openSnackBar(`Photo uploaded`);
        const title = getValues("title");
        const newImage: Image = {
          title,
          imageUrl: currentSelectedPhoto?.toString() ?? "",
          signedId: blob.signed_id,
          markedForDestruction: false,
        };
        onAddImage(newImage);
        reset();
        setCurrentSelectedPhoto(null);
        setLoading(false);
        onClose();
      }
    });
  };

  return (
    <Dialog
      open={visible}
      onClose={() => {
        setCurrentSelectedPhoto(null);
        setPreviewPhoto("");
        onClose();
      }}
      maxWidth="sm"
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box className={classes.root}>
          <Box className={classes.header}>
            <Typography className={classes.headerText}>Upload photo</Typography>
            <IconButton onClick={onClose} size="large">
              <CloseIcon />
            </IconButton>
          </Box>
          <Box
            className={classes.body}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            <Box
              component={ButtonBase}
              className={cx(classes.uploadButton, {
                [classes.hidden]: !hovered,
              })}
              onClick={() => {
                fileInputRef?.current?.click();
              }}
            >
              <ImageIcon />
              <Typography>Select photo</Typography>
            </Box>
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              {...rest}
              onChange={onChangePhoto}
              ref={(e) => {
                ref(e);
                fileInputRef.current = e;
              }}
            />

            {previewPhoto ? (
              <img src={previewPhoto} className={classes.image} />
            ) : (
              <>
                <StyledImage />
              </>
            )}
          </Box>
          <FormHelperText error={Boolean(errors.file)}>
            {errors.file}
          </FormHelperText>
          <Box width="100%" marginBottom={theme.spacing(1)}>
            <Typography color="textSecondary" gutterBottom>
              File title
            </Typography>
            <StyledTextField
              className={classes.input}
              placeholder="Enter title"
              {...register("title", { required: true })}
            />
            <FormHelperText error={Boolean(errors.title)}>
              {errors.title?.message}
            </FormHelperText>
          </Box>
          <StyledSaveButton type="submit" disabled={loading || !isValid}>
            {loading ? `Loading...` : `Upload`}
          </StyledSaveButton>
        </Box>
      </form>

      <SimpleSnackBar />
    </Dialog>
  );
};

export default UploadPhotoModal;
